import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Headphones, ChatCircleText, CheckCircle } from "@phosphor-icons/react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { useAppAuthed } from "../lib/gate";
import StreakBadge from "../components/StreakBadge";
import RewardsCard from "../components/RewardsCard";

const hoursUntil = (iso) => Math.max(1, Math.round((new Date(iso) - Date.now()) / 3600000));

export default function CheckIn() {
  const [c, setC] = useState(null);
  const [resp, setResp] = useState("");
  const [saving, setSaving] = useState(false);
  const [audioErr, setAudioErr] = useState(false);

  const load = () => api.get("/app/checkin").then((r) => { setC(r.data); setResp(r.data.response || ""); }).catch(() => toast.error("Could not load your check-in."));
  const authed = useAppAuthed();
  useEffect(() => { if (authed) load(); /* eslint-disable-next-line */ }, [authed]);

  const complete = async () => {
    setSaving(true);
    try {
      await api.post("/app/checkin", { period: c.period, response: resp });
      toast.success("Check-in complete. See you at the next check-in.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
      if (e?.response?.status === 409) load();
    } finally { setSaving(false); }
  };

  const ep = c?.episode;
  const audio = ep && `${process.env.REACT_APP_BACKEND_URL}/api/series/${ep.series_id}/episode/${ep.episode_number}/audio`;

  return (
    <div data-testid="checkin-page" className="min-h-screen bg-exodus-bg text-exodus-text">
      <Nav />
      <main className="max-w-3xl mx-auto px-5 sm:px-8 pt-28 lg:pt-36 pb-24">
        <p className="eyebrow"><span className="gold-divider" />Your 4-Minute Check-In</p>
        <h1 className="font-display text-4xl sm:text-5xl mt-4 leading-[1]">One question. One audio. <span className="gold-text italic">One next step.</span></h1>
        {!c ? <p className="text-exodus-muted mt-10">Loading…</p> : (
          <div className="space-y-6 mt-12">
            <StreakBadge streak={c.streak} />
            <RewardsCard rewards={c.rewards} streak={c.streak} />
            <section data-testid="checkin-question" className="card-surface p-6 sm:p-8">
              <p className="eyebrow flex items-center gap-2"><ChatCircleText size={14} /> 01 · One question</p>
              <h2 className="font-display text-2xl sm:text-3xl mt-3">{c.question}</h2>
              <textarea data-testid="checkin-response" rows={3} className="input-exodus mt-5" placeholder="Jot a quick thought (optional, private)…"
                value={resp} onChange={(e) => setResp(e.target.value)} disabled={c.completed} />
            </section>
            <section data-testid="checkin-audio" className="card-surface p-6 sm:p-8">
              <p className="eyebrow flex items-center gap-2"><Headphones size={14} /> 02 · One audio{c.lane_label ? ` · picked for ${c.lane_label}` : ""}</p>
              <h3 className="font-display text-2xl mt-3">{ep.title}</h3>
              <p className="text-sm text-exodus-muted mt-1">{ep.series_title} · Episode {ep.episode_number}</p>
              {ep.hook && <p className="text-sm text-exodus-text/90 mt-3 italic">{ep.hook}</p>}
              {audioErr ? (
                <p data-testid="checkin-audio-fallback" className="text-sm text-exodus-muted mt-5 border border-exodus-border p-4">Audio for this episode is being prepared. Read the episode summary above, or open the Series player to try again shortly.</p>
              ) : (
                <audio data-testid="checkin-audio-player" controls preload="none" src={audio} onError={() => setAudioErr(true)} className="w-full mt-5" />
              )}
              <Link to={`/series/${ep.series_id}?resume=${ep.episode_number}`} className="text-xs gold-text hover:underline mt-3 inline-block">Open in the Series player</Link>
            </section>
            <section data-testid="checkin-next-step" className="border border-exodus-gold bg-exodus-elevated p-6 sm:p-8">
              <p className="eyebrow">03 · One next step</p>
              <Link data-testid="checkin-next-action" to={c.next_action.href} className="btn-gold mt-5 w-full sm:w-auto justify-center">
                {c.next_action.label} <ArrowRight size={16} weight="bold" />
              </Link>
            </section>
            {c.completed ? (
              <p data-testid="checkin-completed" className="flex items-center gap-2 text-sm text-exodus-emerald">
                <CheckCircle size={18} weight="fill" /> Check-in complete. Your next one unlocks in about {hoursUntil(c.next_checkin_at)} hours.
              </p>
            ) : (
              <button data-testid="checkin-complete-btn" onClick={complete} disabled={saving} className="btn-ghost">{saving ? "Saving…" : "Mark check-in complete"}</button>
            )}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
